import { ExtendedAddress } from '@porterin/booking-flow/src/order-review/domain/entities/ExtendedAddress';
import { Waypoint } from '@porterin/booking-flow/src/order-review/domain/entities/Waypoint';
import {
  LocationAction,
  LocationContextProps,
  LocationProviderProps,
  LocationState,
  Step,
} from '@porterin/component-library/src/location-accuracy//types/LocationContext.types';
import React, {
  createContext,
  useReducer,
  useContext,
  useCallback,
} from 'react';

const getInitialState = (waypoint: Waypoint): LocationState => ({
  step: 'SelectLocation',
  waypoint,
  address: null,
  mapCenter: { lat: 0, lng: 0 },
});

function reducer(state: LocationState, action: LocationAction): LocationState {
  switch (action.type) {
    case 'SET_ADDRESS':
      return { ...state, address: action.payload };
    case 'SET_MAP_CENTER':
      return { ...state, mapCenter: action.payload };
    case 'SET_STEP':
      return { ...state, step: action.payload };
    default:
      return state;
  }
}

const LocationContext = createContext<LocationContextProps | undefined>(
  undefined,
);

export const LocationProvider: React.FC<LocationProviderProps> = ({
  waypoint,
  close_modal,
  on_confirm,
  children,
}) => {
  const [state, dispatch] = useReducer(reducer, getInitialState(waypoint));

  const selectLocation = useCallback((address: ExtendedAddress) => {
    dispatch({ type: 'SET_ADDRESS', payload: address });
    dispatch({
      type: 'SET_MAP_CENTER',
      payload: { lat: address.location.lat, lng: address.location.lng },
    });
    dispatch({ type: 'SET_STEP', payload: 'LocationAccuracy' });
  }, []);

  const confirmLocation = useCallback((address: ExtendedAddress) => {
    on_confirm(address);
    close_modal();
  }, []);

  const updateStep = useCallback((step: Step) => {
    dispatch({ type: 'SET_STEP', payload: step });
  }, []);

  return (
    <LocationContext.Provider
      value={{ state, selectLocation, confirmLocation, updateStep }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export function useLocation(): LocationContextProps {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
}
